import { HereResult } from '@user/store/services/hereGeoCoder'
import { UseDataReturn } from './useData'
import { useQueryPlaces } from '@user/components/Formik/AddressFields/hooks'
import { useSetMapView } from '@user/components/HereMap/hooks/useSetMapView'
import { useDispatch } from 'react-redux'
import { setSearchTargetLocation } from '@user/store/map/slice'
import { useGetMapBoundary } from '@user/components/HereMap/hooks/useGetMapBoundary'
import { useMapLibreBoundary } from '@user/components/HereMap/hooks/useMapLibreBoundary'
import { logger } from '@user/utils/logger'

type Props = Pick<
  UseDataReturn,
  'setSearchText' | 'addRecentSearch' | 'clearRecentSearches' | 'recentSearches'
> & {
  map: H.Map | null
  onClose: () => void
  onFoundLocation: () => void
  onPanToLocation?: (result: HereResult) => Promise<void>
}

export function useHandlers(props: Props) {
  const {
    setSearchText,
    addRecentSearch,
    map,
    onClose,
    onFoundLocation,
    onPanToLocation,
  } = props
  const dispatch = useDispatch()

  const { results, execute } = useQueryPlaces()

  const { setMapView } = useSetMapView({ map })
  const { drawBoundaryForHereResult } = useGetMapBoundary()
  const { drawBoundaryForHereResult: drawMapLibreBoundary } =
    useMapLibreBoundary()

  async function drawBoundaryIfAny(hereResult: HereResult) {
    if (!map) {
      // MapLibre path: pan to frame the region (existing behaviour), then draw
      // the boundary polygon. The polygon draw is fire-and-forget so it doesn't
      // change the pan → setSearchTargetLocation ordering the caller relies on.
      await onPanToLocation?.(hereResult)
      void drawMapLibreBoundary(hereResult)
      return
    }
    try {
      await drawBoundaryForHereResult(map, hereResult, {
        fitView: true,
      })
    } catch (err) {
      // Swallow errors so search flow continues; optionally log for diagnostics
      logger.warn('drawBoundaryIfAny failed:', err)
    }
  }

  function handleSearchTextChange(text: string) {
    execute(text)
    setSearchText(text)
  }

  async function handleResultClick(hereResult: HereResult) {
    setSearchText(hereResult.address.label)
    setMapView(hereResult)
    addRecentSearch(hereResult)
    const targetPayload = {
      lat: hereResult.position.lat,
      lng: hereResult.position.lng,
      address: hereResult.address.label,
    }
    if (map) {
      // HERE Maps: dispatch before boundary so searchTargetLocation is set
      // when the post-pan getContactLocationsInArea.fulfilled fires
      dispatch(setSearchTargetLocation(targetPayload))
      await drawBoundaryIfAny(hereResult)
    } else {
      // MapLibre: pan first (awaits moveend), then dispatch so overrideFilters
      // activates only after the viewport has moved to the target
      await drawBoundaryIfAny(hereResult)
      dispatch(setSearchTargetLocation(targetPayload))
    }
    onClose()
    onFoundLocation()
  }

  async function handleRecentClick(hereResult: HereResult) {
    setSearchText(hereResult.address.label)
    setMapView(hereResult)
    const targetPayload = {
      lat: hereResult.position.lat,
      lng: hereResult.position.lng,
      address: hereResult.address.label,
    }
    if (map) {
      dispatch(setSearchTargetLocation(targetPayload))
      await drawBoundaryIfAny(hereResult)
    } else {
      await drawBoundaryIfAny(hereResult)
      dispatch(setSearchTargetLocation(targetPayload))
    }
    onClose()
    onFoundLocation()
  }

  async function handleEnterPress() {
    if (results.length > 0) {
      const hereResult = results[0]
      setSearchText(hereResult.address.label)
      setMapView(hereResult)
      await drawBoundaryIfAny(hereResult)
      addRecentSearch(hereResult)
      dispatch(
        setSearchTargetLocation({
          lat: hereResult.position.lat,
          lng: hereResult.position.lng,
          address: hereResult.address.label,
        }),
      )
      onClose()
      onFoundLocation()
    }
  }

  return {
    handleSearchTextChange,
    results,
    handleRecentClick,
    handleResultClick,
    handleEnterPress,
  }
}
